import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { useSwipeNav } from "../hooks/useSwipeNav";
import { fetchLeaderboard, leaderboardOptOut, leaderboardOptIn } from "../lib/api";
import { formatDuration } from "../lib/format";
import { getUtcToday, addDays } from "../lib/constants";
import { LAUNCH_DATE } from "@griddle/generator";
import { LeaderboardView, LeaderboardEntry, LeaderboardResponse } from "../types";

const VALID_VIEWS = new Set<LeaderboardView>(["time", "stars", "winrate", "streak", "wins"]);
import styles from "./Leaderboard.module.css";

const VIEWS: { key: LeaderboardView; label: string }[] = [
  { key: "time", label: "Time" },
  { key: "stars", label: "Stars" },
  { key: "winrate", label: "Win Rate" },
  { key: "streak", label: "Streak" },
  { key: "wins", label: "Wins" },
];

/** Days since launch, used to rotate the default view */
function daysSinceLaunch(): number {
  const launch = new Date(LAUNCH_DATE + "T00:00:00Z").getTime();
  const now = new Date(getUtcToday() + "T00:00:00Z").getTime();
  return Math.floor((now - launch) / (24 * 60 * 60 * 1000));
}

function starString(count: number): string {
  return "\u2605".repeat(count) + "\u2606".repeat(6 - count);
}

function isDailyView(view: LeaderboardView): boolean {
  return view === "time" || view === "stars";
}

interface LeaderboardProps {
  size: "7x7" | "9x9";
}

export function Leaderboard({ size }: LeaderboardProps) {
  const { user, updateUser } = useAuth();
  const [view, setView] = useState<LeaderboardView>(() => {
    const params = new URLSearchParams(window.location.search);
    const v = params.get("view") as LeaderboardView | null;
    return v && VALID_VIEWS.has(v) ? v : VIEWS[daysSinceLaunch() % VIEWS.length].key;
  });
  const [date, setDate] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const d = params.get("date");
    return d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : getUtcToday();
  });
  const [data, setData] = useState<LeaderboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const today = useMemo(getUtcToday, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const dateParam = isDailyView(view) ? date : undefined;
    fetchLeaderboard(size, view, dateParam)
      .then(setData)
      .catch(() => setError("Failed to load leaderboard"))
      .finally(() => setLoading(false));
  }, [size, view, date]);

  const handlePrevDate = useCallback(() => {
    setDate((d) => {
      const prev = addDays(d, -1);
      return prev >= LAUNCH_DATE ? prev : d;
    });
  }, []);

  const handleNextDate = useCallback(() => {
    setDate((d) => {
      const next = addDays(d, 1);
      return next <= today ? next : d;
    });
  }, [today]);

  const handleOptOut = useCallback(async () => {
    await leaderboardOptOut();
    updateUser({ leaderboardName: null });
  }, [updateUser]);

  const handleOptIn = useCallback(async () => {
    const result = await leaderboardOptIn();
    updateUser({ leaderboardName: result.leaderboardName });
  }, [updateUser]);

  const formattedDate = useMemo(() => {
    const d = new Date(date + "T00:00:00Z");
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
  }, [date]);

  // Swipe left/right to page the date on daily views (same as the ‹ › buttons).
  const swipe = useSwipeNav((dir) => {
    if (!isDailyView(view)) return;
    if (dir === "next") handleNextDate();
    else handlePrevDate();
  });

  return (
    <div className={styles.leaderboard} {...swipe}>
      <div className={styles.header}>
        <div className={styles.spacer} />
        <h2 className={styles.title}>Leaderboard</h2>
        <div className={styles.spacer} />
      </div>

      <div className={styles.viewPicker}>
        {VIEWS.map((v) => (
          <button
            key={v.key}
            className={`${styles.viewPill} ${view === v.key ? styles.viewPillActive : ""}`}
            onClick={() => setView(v.key)}
          >
            {v.label}
          </button>
        ))}
      </div>

      {isDailyView(view) && (
        <div className={styles.dateNav}>
          <button
            className={styles.dateNavButton}
            onClick={handlePrevDate}
            disabled={date <= LAUNCH_DATE}
            aria-label="Previous day"
          >
            &#8249;
          </button>
          <span className={styles.dateLabel}>{formattedDate}</span>
          <button
            className={styles.dateNavButton}
            onClick={handleNextDate}
            disabled={date >= today}
            aria-label="Next day"
          >
            &#8250;
          </button>
        </div>
      )}

      {user && (
        <div className={styles.optOutBar}>
          {user.leaderboardName ? (
            <>
              Playing as <strong>{user.leaderboardName}</strong>
              <button className={styles.optOutButton} onClick={handleOptOut}>Hide me</button>
            </>
          ) : (
            <>
              You're hidden from the leaderboard
              <button className={styles.optOutButton} onClick={handleOptIn}>Join</button>
            </>
          )}
        </div>
      )}

      {loading && <p className={styles.loading}>Loading...</p>}
      {error && <p className={styles.empty}>{error}</p>}
      {!loading && !error && data && (
        <LeaderboardTable entries={data.entries} view={view} currentUserId={user?.id ?? null} />
      )}
    </div>
  );
}

function LeaderboardTable({ entries, view, currentUserId }: {
  entries: LeaderboardEntry[];
  view: LeaderboardView;
  currentUserId: string | null;
}) {
  if (entries.length === 0) {
    return <p className={styles.empty}>No entries yet. Be the first!</p>;
  }

  return (
    <div className={styles.list}>
      {entries.map((entry) => (
        <LeaderboardRow
          key={entry.userId}
          entry={entry}
          view={view}
          isCurrentUser={entry.userId === currentUserId}
        />
      ))}
    </div>
  );
}

function LeaderboardRow({ entry, view, isCurrentUser }: {
  entry: LeaderboardEntry;
  view: LeaderboardView;
  isCurrentUser: boolean;
}) {
  return (
    <div className={`${styles.row} ${isCurrentUser ? styles.rowHighlight : ""}`}>
      <span className={`${styles.rank} ${entry.rank <= 3 ? styles.rankTop : ""}`}>
        {entry.rank}
      </span>
      {entry.avatarUrl ? (
        <img className={styles.avatar} src={entry.avatarUrl} alt="" />
      ) : (
        <div className={styles.avatarPlaceholder}>
          {entry.leaderboardName.charAt(0)}
        </div>
      )}
      <span className={styles.name}>{entry.leaderboardName}</span>
      <MetricDisplay entry={entry} view={view} />
    </div>
  );
}

function AttemptBadge({ attempts }: { attempts?: number }) {
  if (!attempts || attempts <= 1) return null;
  return <span className={styles.attempts}>x{attempts}</span>;
}

function MetricDisplay({ entry, view }: { entry: LeaderboardEntry; view: LeaderboardView }) {
  switch (view) {
    case "time":
      return (
        <>
          <AttemptBadge attempts={entry.attempts} />
          <span className={styles.metric}>{formatDuration(entry.elapsedMs ?? 0)}</span>
          <span className={styles.starsDisplay}>{starString(entry.stars ?? 0)}</span>
        </>
      );
    case "stars":
      return (
        <>
          <AttemptBadge attempts={entry.attempts} />
          <span className={styles.starsDisplay}>{starString(entry.stars ?? 0)}</span>
          <span className={styles.metricSecondary}>{formatDuration(entry.elapsedMs ?? 0)}</span>
        </>
      );
    case "winrate":
      return (
        <>
          <span className={styles.metric}>{entry.winPercent ?? 0}%</span>
          <span className={styles.metricSecondary}>{(entry.avgStars ?? 0).toFixed(1)} avg</span>
        </>
      );
    case "streak":
      return (
        <>
          <span className={styles.metric}>{entry.currentPlayStreak ?? 0} days</span>
          <span className={styles.metricSecondary}>best: {entry.longestPlayStreak ?? 0}</span>
        </>
      );
    case "wins":
      return (
        <>
          <span className={styles.metric}>{entry.longestWinStreak ?? 0} wins</span>
          <span className={styles.metricSecondary}>current: {entry.currentWinStreak ?? 0}</span>
        </>
      );
  }
}

